import { Contact, ContactType } from '@api/generated/graphql';
import { ErrorLike } from '@apollo/client';
import ErrorComponent from '@components/ErrorComponent';
import {
  Button,
  Headline,
  Link,
  Spinner,
  Stack,
  Tooltip,
} from '@servicetitan/design-system';
import { inboxLocators } from '@locators/inbox';
import styles from '../../CustomerDetails.module.css';
import CustomerCardWrapper from '../CustomerCardWrapper/CustomerCardWrapper';
import { CustomerDetailsField } from '../CustomerDetailsField';
import { SpinnerStack } from './CustomerContactMethods.styled';
import strings from './strings';

interface CustomerContactsProps {
  contacts: Contact[];
  contactsLoading: boolean;
  contactsError?: ErrorLike;
  onEditClicked: () => void;
  onViewAllClicked: () => void;
}

export const CustomerContacts = ({
  contacts,
  contactsLoading,
  onViewAllClicked,
  onEditClicked,
  contactsError,
}: CustomerContactsProps) => {
  const customerPhones = contacts.filter((cm) => cm.type === ContactType.Phone);
  const firstPhone = customerPhones.length > 0 ? customerPhones[0] : undefined;

  const additionalPhoneNumbersCount =
    customerPhones.length > 1 ? customerPhones.length - 1 : 0;

  const customerEmails = contacts.filter((cm) => cm.type === ContactType.Email);
  const firstEmail = customerEmails.length > 0 ? customerEmails[0] : undefined;

  const additionalEmailsCount =
    customerEmails.length > 1 ? customerEmails.length - 1 : 0;

  const customerMobiles = contacts.filter(
    (cm) => cm.type === ContactType.MobilePhone,
  );

  const firstMobile =
    customerMobiles.length > 0 ? customerMobiles[0] : undefined;

  const additionalMobileNumbersCount =
    customerMobiles.length > 1 ? customerMobiles.length - 1 : 0;

  const shouldShowViewAllButton =
    additionalPhoneNumbersCount > 0 ||
    additionalEmailsCount > 0 ||
    additionalMobileNumbersCount > 0;

  return (
    <CustomerCardWrapper>
      <Stack
        className={styles.customerHeader}
        justifyContent='space-between'
        alignItems='center'
      >
        <Headline className='m-0' size='small'>
          Contact Methods
        </Headline>
        <Tooltip el='div' text={strings.labels.editContactsButton} portal>
          <Button
            id={inboxLocators.customerSearch.editIconBtn}
            fill='subtle'
            color='primary'
            size='xsmall'
            iconName='edit'
            onClick={onEditClicked}
            aria-label={strings.labels.editContactsButton}
          />
        </Tooltip>
      </Stack>
      <Stack
        justifyContent={contactsLoading ? 'center' : 'space-between'}
        alignItems='center'
        spacing={2}
      >
        {contactsLoading && (
          <SpinnerStack justifyContent='center' alignItems='center'>
            <Spinner size='medium' />
          </SpinnerStack>
        )}
        {contactsError && (
          <Stack className='w-100' justifyContent='center' alignItems='center'>
            <ErrorComponent noImage />
          </Stack>
        )}
        {!contactsLoading && !contactsError && (
          <Stack
            direction='row'
            spacing={2}
            className='w-100'
            justifyContent='space-between'
          >
            <Stack direction='column' spacing={2}>
              <CustomerDetailsField
                title='Phone'
                content={firstPhone ?? 'None'}
                contentLocator={
                  inboxLocators.customerSearch.contactMethods.phone
                }
                badgeValue={additionalPhoneNumbersCount}
              />
              <CustomerDetailsField
                title='Email'
                content={firstEmail ?? 'None'}
                contentLocator={
                  inboxLocators.customerSearch.contactMethods.email
                }
                badgeValue={additionalEmailsCount}
              />
            </Stack>
            <Stack
              direction='column'
              spacing={2}
              justifyContent='space-between'
              alignItems='flex-end'
            >
              <CustomerDetailsField
                title='Mobile'
                content={firstMobile ?? 'None'}
                badgeValue={additionalMobileNumbersCount}
              />
              {shouldShowViewAllButton && (
                <Link
                  className={`c-blue-500-i t-truncate ${styles.contactMethodLink}`}
                  onClick={onViewAllClicked}
                  aria-label={strings.labels.viewAll}
                >
                  {strings.labels.viewAllButton}
                </Link>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </CustomerCardWrapper>
  );
};

export default CustomerContacts;
